import React, { useState, useEffect } from 'react';
import { Search, Menu, User } from 'lucide-react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';

// Services
import { getProperties } from './services/apiService';

// Types
import { Property } from "./types";

// Import Componenti
import HomeContent from "./components/HomeContent";
import VillasContent from "./components/VillasContent";
import RatesContent from "./components/RatesContent";
import LocationContent from "./components/LocationContent";
import AboutContent from "./components/AboutContent";
import FaqContent from "./components/FaqContent";
import ContactContent from "./components/ContactContent";
import PropertyDetails from "./components/PropertyDetail";
import TermsContent from "./components/TermsContent";
import PrivacyPolicyContent from "./components/PrivacyPolicyContent";

// Import Pagine Intere
import AdminDashboard from "./pages/AdminDashboard";
import Login from "./pages/Login";
import OwnersContent from "./pages/OwnersContent";
import CookieBanner from "./components/CookieBanner";
import ComplianceContent from "./pages/ComplianceContent";
import NewsletterWidget from "./pages/NewsletterWidget";
import OwnerPropertyForm from "./pages/OwnerPropertyForm";
import AgreementContent from "./pages/AgreementContent";
import BlogContent from "./components/BlogContent";
import BlogPostDetail from "./components/BlogPostDetail";
import GuestManualView from "./pages/GuestManualView";
import OwnerManualEdit from "./pages/OwnerManualEdit";
import OwnerOnboarding from "./pages/OwnerOnboarding";

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // --- STATI UI ---
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(() => !!localStorage.getItem('authToken'));

  // --- STATI DATI ---
  const [allProperties, setAllProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [isSearching, setIsSearching] = useState(false);

  // --- STATO RICERCA ---
  const [searchParams, setSearchParams] = useState({
    location: '',
    checkIn: '',
    checkOut: '',
    guests: 1
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // --- FUNZIONE FETCH DAL BACKEND ---
  const fetchProperties = async (params = {}) => {
    setIsSearching(true);
    try {
      const data = await getProperties(params) as unknown as Property[];
      if (data) {
        setAllProperties(data);
        setFilteredProperties(data);
      }
    } catch (error) {
      console.error("Error loading properties:", error);
    } finally {
      setIsSearching(false);
    }
  };

  // --- 1. CARICAMENTO INIZIALE ---
  useEffect(() => {
    if (!location.pathname.startsWith('/admin') && !location.pathname.startsWith('/owner-onboarding') && allProperties.length === 0) {
      if (location.pathname === '/villas' && !location.search) {
        fetchProperties();
      } else if (location.pathname !== '/villas') {
        fetchProperties();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- 2. LISTENER URL PER RICERCA AUTOMATICA ---
  useEffect(() => {
    if (location.pathname === '/villas') {
      const params = new URLSearchParams(location.search);
      const pCheckIn = params.get('checkIn');
      const pCheckOut = params.get('checkOut');
      const pGuests = params.get('guests');
      const pLocation = params.get('location') || '';

      if (pCheckIn && pCheckOut) {
        const guestsNum = pGuests ? parseInt(pGuests) : 1;
        setSearchParams({
          location: pLocation,
          checkIn: pCheckIn,
          checkOut: pCheckOut,
          guests: guestsNum
        });
        fetchProperties({
          location: pLocation,
          checkIn: pCheckIn,
          checkOut: pCheckOut,
          guests: guestsNum
        });
      }
    }
  }, [location.pathname, location.search]);

  // --- GESTIONE SEARCH ---
  const handleSearch = () => {
    if (!searchParams.checkIn || !searchParams.checkOut) {
      alert("Please select Check-in and Check-out dates.");
      return;
    }
    if (!searchParams.guests || searchParams.guests < 1) {
      alert("Please select at least 1 guest.");
      return;
    }

    if (location.pathname !== '/villas') {
      navigate('/villas');
    }

    fetchProperties({
      location: searchParams.location,
      checkIn: searchParams.checkIn,
      checkOut: searchParams.checkOut,
      guests: searchParams.guests
    });
  };

  // --- FILTRO CATEGORIE ---
  useEffect(() => {
    let result = allProperties;
    if (activeCategory === 'Owner Approval') {
      result = result.filter(p => p.tag === 'Owner Approval' /*|| (p.price && p.price > 800)*/);
    } else if (activeCategory === 'Direct Booking') {
      result = result.filter(p => p.tag === 'Direct Booking' /*|| (p.price && p.price <= 800)*/);
    }
    setFilteredProperties(result);
  }, [activeCategory, allProperties]);

  // --- GESTIONE SCROLL HEADER ---
  useEffect(() => {
    const handleScroll = () => {
      if (location.pathname !== '/') {
        setIsScrolled(true);
        return;
      }
      setIsScrolled(window.scrollY > 50);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const goTo = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const selectCategoryAndNavigate = (category: string) => {
    setActiveCategory(category);
    goTo('/villas');
  };

  const headerClass = isScrolled ? "bg-white shadow-sm py-4 text-[#1a1a1a]" : "bg-transparent py-6 text-white";
  const logoTextClass = isScrolled ? "text-primary" : "text-white";
  const logoSubTextClass = isScrolled ? "text-secondary" : "text-white";
  const showCompactSearch = isScrolled && location.pathname === '/';

  if (location.pathname === '/login') return <Login />;
  if (location.pathname.startsWith('/admin')) {
    return isAdmin ? <AdminDashboard /> : <Navigate to="/login" replace />;
  }
  if (location.pathname.startsWith('/owner-onboarding/')) {
    return (
      <Routes>
        <Route path="/owner-onboarding/:slug" element={<OwnerOnboarding />} />
      </Routes>
    );
  }
  if (location.pathname.startsWith('/manual/edit/')) {
    return (
      <Routes>
        <Route path="/manual/edit/:editSlug" element={<OwnerManualEdit />} />
      </Routes>
    );
  }
  if (location.pathname.startsWith('/manual/')) {
    return (
      <Routes>
        <Route path="/manual/:guestSlug" element={<GuestManualView />} />
      </Routes>
    );
  }

  return (
    // Aggiunto overflow-x-hidden e w-full per evitare scroll orizzontale
    <div className="text-[#222222] bg-white min-h-screen flex flex-col font-sans overflow-x-hidden w-full">

      {/* HEADER */}
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 max-w-[100vw] ${headerClass}`}>
        {/* Padding ridotto su mobile (px-4) e normale su desktop (md:px-6) */}
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 flex justify-between items-center">

          {/* LOGO */}
          <div className="flex items-center gap-2 md:gap-3 cursor-pointer group z-50" onClick={() => goTo('/')}>
            <img
              src={isScrolled ? "/logo1.webp" : "/img/Logo-HOME_anotherhome.png"}
              alt="Another Home Logo"
              className="w-8 h-8 md:w-10 md:h-10 object-contain transition-opacity duration-300"
            />
            {/* Il testo è nascosto su mobile (hidden), quindi non causa overflow */}
            <div className="hidden md:flex flex-col">
              <span className={`text-2xl font-serif font-bold tracking-tight transition-colors duration-300 leading-none ${logoTextClass}`}>Another Home</span>
              <span className={`text-[0.6rem] uppercase tracking-widest font-sans font-semibold opacity-80 mt-1 transition-colors duration-300 ${logoSubTextClass}`}>Hospitality Services</span>
            </div>
          </div>

          {/* SEARCH COMPATTA (Nascosta su mobile) */}
          <div
            onClick={() => goTo('/villas')}
            className={`hidden md:flex items-center border border-gray-200 rounded-full py-2.5 px-4 shadow-sm hover:shadow-md transition-all cursor-pointer divide-x divide-gray-300 bg-white ml-8 duration-300 ${showCompactSearch ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}
          >
            <div className="px-4 text-sm font-semibold">Start your search</div>
            <div className="bg-brand p-2 rounded-full text-white ml-2"><Search className="w-3 h-3 stroke-white" /></div>
          </div>

          {/* MENU A DESTRA */}
          <div className="flex items-center gap-2 md:gap-4 z-50">
            {isAdmin ? (
              <button onClick={() => window.location.href = '/admin'} className="hidden md:flex items-center gap-2 text-xs font-bold px-3 py-1 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 border border-gray-300 transition-colors">
                DASHBOARD
              </button>
            ) :
              <button onClick={() => window.location.href = '/login'} className="hidden md:flex items-center gap-2 text-xs font-bold px-3 py-1 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 border border-gray-300 transition-colors">
                Login
              </button>
            }

            <div className="relative">
              <div className="flex items-center gap-2 border border-gray-300 rounded-full p-1 pl-3 hover:shadow-md transition-shadow bg-white cursor-pointer" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                <Menu className="w-4 h-4 text-gray-600" />
                <div className="bg-gray-500 text-white rounded-full p-2"><User className="w-4 h-4 fill-white stroke-white" /></div>
              </div>

              {isMenuOpen && (
                <div className="absolute top-14 right-0 bg-white rounded-xl shadow-xl border border-gray-100 w-64 py-2 overflow-hidden origin-top-right text-gray-800 animate-fade-in z-[60]">
                  <button onClick={() => goTo('/')} className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-semibold transition-colors">Home</button>
                  <button onClick={() => goTo('/villas')} className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-semibold transition-colors">Our Properties</button>
                  <button onClick={() => goTo('/owners')} className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-semibold transition-colors">Owners</button>
                  <button onClick={() => goTo('/rates')} className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-semibold transition-colors">Services</button>
                  <button onClick={() => goTo('/about')} className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-semibold transition-colors">About Us</button>
                  <button onClick={() => goTo('/faq')} className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-semibold transition-colors">FAQ</button>
                  <button onClick={() => goTo('/blog')} className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-semibold transition-colors">Blog</button>
                  <button onClick={() => goTo('/contact')} className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-semibold transition-colors">Contact</button>
                  {isAdmin && (
                    <button onClick={() => window.location.href = '/admin'} className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-bold text-brand transition-colors border-t bg-gray-50">
                      Go to Dashboard
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* CONTENUTO */}
      <main className="flex-grow w-full">
        <Routes>
          <Route path="/" element={
            <HomeContent
              navigate={(page) => goTo(page === 'home' ? '/' : `/${page}`)}
              onCategorySelect={selectCategoryAndNavigate}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              onSearch={handleSearch}
            />
          } />
          <Route path="/agreement" element={<AgreementContent />} />
          <Route path="/villas" element={
            <VillasContent
              properties={filteredProperties}
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              onSearch={handleSearch}
              loading={isSearching}
            />
          } />
          <Route path="/details/:id" element={<PropertyDetails />} />
          <Route path="/rates" element={<RatesContent />} />
          <Route path="/location" element={<LocationContent />} />
          <Route path="/about" element={<AboutContent />} />
          <Route path="/faq" element={<FaqContent />} />
          <Route path="/contact" element={<ContactContent />} />
          <Route path="/terms" element={<TermsContent />} />
          <Route path="/privacy" element={<PrivacyPolicyContent />} />
          <Route path="/owners" element={<OwnersContent />} />
          <Route path="/compliance" element={<ComplianceContent />} />
          <Route path="/owner-submission" element={<OwnerPropertyForm />} />
          <Route path="/blog" element={<BlogContent />} />
          <Route path="/blog/:slug" element={<BlogPostDetail />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <NewsletterWidget />

      <footer className="bg-[#1F2A33] border-t border-[#D6D1CB]/10 py-16 px-6 w-full font-sans text-[#F6F4F1]">

        {/* TOP SECTION: Disclaimer */}
        <div className="max-w-7xl mx-auto mb-12 border-b border-[#D6D1CB]/10 pb-12">
          <h5 className="text-[#E84C5B] font-bold tracking-widest uppercase text-xs mb-4">Website Disclaimer & Legal Notice</h5>
          <div className="text-[#D6D1CB] text-[10px] leading-relaxed opacity-80 space-y-3 text-justify">
            <p>
              ANOTHER HOME provides co‑hosting and hospitality services and is not a real estate broker or leasing agent. Where services require licensure, ANOTHER HOME may coordinate with appropriately licensed real estate professionals or other qualified third‑party providers at the owner’s direction.
              All information displayed on this website is provided for general informational purposes only and is sourced from materials believed to be reliable; however, accuracy, completeness, and timeliness cannot be guaranteed. Users are encouraged to independently verify all information prior to making property ownership or hosting‑related decisions.
            </p>
            <p>
              Property information is subject to errors, omissions, changes in availability, or withdrawal without notice. Descriptions, images, measurements, and square footage are approximate and should be independently confirmed. No representation or warranty is made regarding the accuracy of any content.
            </p>
            <p>
              All website content, including text, photographs, graphics, and branding, is the property of ANOTHER HOME or its respective content providers and is protected by applicable copyright and intellectual property laws. Unauthorized reproduction, redistribution, or commercial use of any materials is strictly prohibited.
            </p>
            <p>
              ANOTHER HOME fully supports and complies with all applicable Fair Housing laws and regulations and is committed to providing equal access and non‑discriminatory practices.
            </p>
            <p>
              Users are advised to consult their own legal, tax, and financial advisors regarding ownership, tax, and regulatory considerations applicable to their property. This website is intended for informational and personal use only.
            </p>
            <p>
              If you experience difficulty accessing any part of this website or require assistance due to a visual impairment or use of assistive technologies, please contact us and we will be happy to provide support.            </p>
          </div>
        </div>

        {/* BOTTOM SECTION: Links & Credits */}
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 text-xs">

          {/* Links */}
          <div className="flex gap-6 font-bold text-[#D6D1CB]">
            <button onClick={() => navigate('privacy')} className="hover:text-[#E84C5B] transition-colors">Privacy Policy</button>
            <button onClick={() => navigate('terms')} className="hover:text-[#E84C5B] transition-colors">Terms & Conditions</button>
            <button onClick={() => navigate('agreement')} className="hover:text-[#E84C5B] transition-colors">Agreements</button>
            <button onClick={() => navigate('compliance')} className="hover:text-[#E84C5B] transition-colors">Compliance</button>
            <button onClick={() => navigate('owner-submission')} className="hover:text-[#E84C5B] transition-colors">Submit Property</button>
          </div>

          {/* Copyright & Credits */}
          <div className="flex flex-col md:flex-row items-center gap-1 md:gap-4 text-[#D6D1CB]/60">
            <span>© 2026 ANOTHER HOME. All rights reserved.</span>
            <span className="hidden md:inline text-[#E84C5B]">•</span>
            <span>
              Created by Axiomgroup.it
            </span>
          </div>
        </div>
      </footer>
      <CookieBanner />

    </div>
  );
};

export default App;